/**
 * Plan 20a, Task 2.3 — Counter bounce hook
 *
 * Factory: createCounterBounceHook(config: UiVfxHookConfig)
 *   Returns a UiVfxHook<HTMLElement, { delta: number }> with id 'counter-bounce'.
 *
 * Behaviour:
 *   play(counter, { delta }):
 *     - Runs a 2-tween gsap timeline: scale 1.0 → 1.4 (125ms) then 1.4 → 1.0 (125ms)
 *       Total natural duration = 250ms using back.out(2.5) easing (animation.easing.bouncy).
 *     - delta > 0  → sets CSS variable --counter-tint to POSITIVE_TINT (#FFD700, gold).
 *     - delta < 0  → sets CSS variable --counter-tint to NEGATIVE_TINT (#FF4444, red).
 *     - delta === 0 → no tint change (--counter-tint left unchanged).
 *     Consumer CSS applies the actual color effect via the --counter-tint variable.
 *   dispose():
 *     - Idempotent — kills any in-flight timeline via handle.dispose().
 *
 * Duration / easing tokens are read from data-vfx-* attributes on the target element
 * with hardcoded fallbacks so the hook compiles standalone without a token resolver:
 *   data-vfx-duration-normal → normalMs fallback 250ms
 *   data-vfx-easing-bouncy   → bouncyEase fallback 'back.out(2.5)'
 *
 * Tint values (hardcoded defaults, CSS variable consumers apply actual rendering):
 *   POSITIVE_TINT = '#FFD700' (gold, for delta > 0: coins gained, score up, etc.)
 *   NEGATIVE_TINT = '#FF4444' (red, for delta < 0: HP lost, penalty, etc.)
 *
 * NOTE — completion callbacks:
 *   The gsap-registry sentinel tween extends every timeline to VIRTUAL_DURATION=3600s.
 *   eventCallback('onComplete') on the root timeline fires at 3600s, not at the natural
 *   animation end. Use timeline.call(fn, undefined, absoluteSeconds) instead.
 *   Canonical reference: src/client/renderer/animation/hit-reaction.ts:17-18.
 *
 * NOTE — back.out(2.5) ease string:
 *   GSAP accepts parametrised eases as string literals: 'back.out(2.5)'.
 *   The numeric argument is the overshoot factor; 2.5 gives a moderately punchy bounce.
 *   Verified via Context7 GSAP docs (ease string syntax matches 'back.in(1.2)' pattern
 *   shown in https://gsap.com/docs/v3/Eases/).
 */

import type { UiVfxHook, UiVfxHookConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public opts type
// ─────────────────────────────────────────────────────────────────────────────

export interface CounterBounceOpts {
  /** Signed change in the counter value.
   *  > 0 → gold tint (gain),  < 0 → red tint (loss),  === 0 → no tint change. */
  delta: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tint constants
// ─────────────────────────────────────────────────────────────────────────────

/** CSS color applied to --counter-tint for positive delta (coin gain, score up, etc.). */
const POSITIVE_TINT = '#FFD700'; // gold

/** CSS color applied to --counter-tint for negative delta (HP loss, penalty, etc.). */
const NEGATIVE_TINT = '#FF4444'; // red

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers — token reading (same pattern as button-states.ts)
// ─────────────────────────────────────────────────────────────────────────────

/** Reads a millisecond duration from a data-vfx-* attribute; fallback in ms. */
function readDurationMs(el: HTMLElement, attr: string, fallbackMs: number): number {
  const raw = el.dataset[attr];
  if (raw) {
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return fallbackMs;
}

/** Reads an easing string from a data-vfx-* attribute; falls back to provided default. */
function readEasing(el: HTMLElement, attr: string, fallback: string): string {
  const raw = el.dataset[attr];
  return raw && raw.trim().length > 0 ? raw.trim() : fallback;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createCounterBounceHook(
  config: UiVfxHookConfig,
): UiVfxHook<HTMLElement, CounterBounceOpts> {
  const { gsapRegistry } = config;

  /** In-flight handle; at most one active at a time. */
  let currentHandle: GsapTimelineHandle | null = null;

  /** Kill the current in-flight timeline, if any. */
  function killCurrent(): void {
    if (currentHandle) {
      currentHandle.dispose();
      currentHandle = null;
    }
  }

  function play(target: HTMLElement, opts?: CounterBounceOpts): GsapTimelineHandle {
    // Kill any prior timeline before spawning a new one.
    killCurrent();

    const delta = opts?.delta ?? 0;

    // ── Read timing tokens from element data-attributes (with fallbacks) ──────
    // data-vfx-duration-normal → dataset.vfxDurationNormal
    const normalMs = readDurationMs(target, 'vfxDurationNormal', 250);
    // data-vfx-easing-bouncy   → dataset.vfxEasingBouncy
    const bouncyEase = readEasing(target, 'vfxEasingBouncy', 'back.out(2.5)');

    // Each half-tween is half the total duration
    const halfSec = normalMs / 1000 / 2;

    // ── Set --counter-tint CSS variable based on delta sign ───────────────────
    if (delta > 0) {
      target.style.setProperty('--counter-tint', POSITIVE_TINT);
    } else if (delta < 0) {
      target.style.setProperty('--counter-tint', NEGATIVE_TINT);
    }
    // delta === 0: no tint change

    // ── Build 2-tween bounce timeline ────────────────────────────────────────
    const handle = gsapRegistry.create('counter-bounce');
    const tl = handle.timeline;

    // Tween 1: scale-up 1.0 → 1.4 over halfSec with bouncy ease
    tl.to(
      target,
      {
        scale: 1.4,
        duration: halfSec,
        ease: bouncyEase,
      },
      0,
    );

    // Tween 2: scale-down 1.4 → 1.0 over halfSec with bouncy ease
    tl.to(
      target,
      {
        scale: 1.0,
        duration: halfSec,
        ease: bouncyEase,
      },
      halfSec,
    );

    currentHandle = handle;
    return handle;
  }

  function dispose(): void {
    killCurrent();
  }

  return {
    id: 'counter-bounce',
    play,
    dispose,
  };
}
